import { useMsal } from '@azure/msal-react';
import {
  useAutoImportTaggedMutation,
  useSyncMeetingsMutation,
  useSyncRecurringSeriesMutation,
} from '@consigli/hooks';
import { Meeting } from '@consigli/types';
import { useEffect, useRef, useCallback, useState } from 'react';

export const SYNC_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

interface UseMeetingSyncOptions {
  projectId: number;
  meetings: Meeting[];
  isAuthenticated: boolean;
  isMeetingsLoading: boolean;
}

interface UseMeetingSyncReturn {
  syncWithTeams: () => Promise<void>;
  isSyncing: boolean;
}

export const useMeetingSync = ({
  projectId,
  meetings,
  isAuthenticated,
  isMeetingsLoading,
}: UseMeetingSyncOptions): UseMeetingSyncReturn => {
  const { accounts, instance } = useMsal();
  const [syncMeetings] = useSyncMeetingsMutation();
  const [syncRecurringSeries] = useSyncRecurringSeriesMutation();
  const [autoImportTagged] = useAutoImportTaggedMutation();
  const [isSyncing, setIsSyncing] = useState(false);

  const hasInitialSyncRef = useRef(false);
  const isSyncingInProgressRef = useRef(false);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const meetingsRef = useRef(meetings);
  meetingsRef.current = meetings;

  const syncWithTeams = useCallback(async () => {
    if (!isAuthenticated || accounts.length === 0 || isSyncingInProgressRef.current) {
      return;
    }

    const account = accounts[0];
    if (!account) {
      return;
    }

    isSyncingInProgressRef.current = true;
    setIsSyncing(true);

    try {
      const tokenRequest = {
        scopes: ['Calendars.Read', 'OnlineMeetings.Read', 'User.Read'],
        account,
      };

      const tokenResponse = await instance.acquireTokenSilent(tokenRequest);

      const authParams = {
        projectId,
        msAccessToken: tokenResponse.accessToken,
        userEmail: account.username || '',
        userId: account.localAccountId || '',
      };

      const currentMeetings = meetingsRef.current;
      const meetingIds = currentMeetings.map((meeting) => meeting.id);
      const uniqueSeriesIds = Array.from(
        new Set(
          currentMeetings
            .filter((meeting) => meeting.recurringSeriesId)
            .map((meeting) => meeting.recurringSeriesId as string),
        ),
      );

      const syncPromises: Promise<unknown>[] = [
        syncMeetings({
          ...authParams,
          useDelta: true,
          meetingIds,
        }).unwrap(),
        // Best-effort scan for #ma_{project_id}-tagged meetings. sync_meetings
        // triggers this server-side too, but we enqueue+poll it explicitly so
        // the Meetings list is invalidated once the tagged scan actually
        // completes (the sync task returns before the parallel scan finishes).
        autoImportTagged(authParams).unwrap(),
      ];

      if (uniqueSeriesIds.length > 0) {
        syncPromises.push(
          syncRecurringSeries({
            ...authParams,
            seriesIds: uniqueSeriesIds,
          }).unwrap(),
        );
      }

      await Promise.allSettled(syncPromises);
    } catch (error) {
      console.error('Error syncing meetings:', error);
    } finally {
      isSyncingInProgressRef.current = false;
      setIsSyncing(false);
    }
  }, [
    isAuthenticated,
    accounts,
    instance,
    projectId,
    syncMeetings,
    syncRecurringSeries,
    autoImportTagged,
  ]);

  useEffect(() => {
    if (isAuthenticated && !isMeetingsLoading && !hasInitialSyncRef.current) {
      hasInitialSyncRef.current = true;
      syncWithTeams();
    }
  }, [isAuthenticated, isMeetingsLoading, syncWithTeams]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const startInterval = () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
      intervalIdRef.current = setInterval(() => {
        syncWithTeams();
      }, SYNC_INTERVAL_MS);
    };

    const stopInterval = () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopInterval();
      } else {
        startInterval();
        syncWithTeams();
      }
    };

    if (!document.hidden) {
      startInterval();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, syncWithTeams]);

  return {
    syncWithTeams,
    isSyncing,
  };
};
